import { Button, Dialog, DialogContent, DialogTitle, makeStyles, TextField, Theme } from '@material-ui/core';
import ImportIcon from '@material-ui/icons/Publish';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { transl } from '../../main/transl';
import { useBooleanStateAgg } from '../../mg/useStateAgg';
import { IOcResponse } from '../../services/CommSvc';
import { toasterSvc } from '../../services/ToasterSvc';
import { useOmnistore } from '../../stores/Omnistore';
import { PleaseWait } from '../PleaseWait';
import { ImportFeedbackDialog } from './ImportFeedbackDialog';

const useStyles = makeStyles((theme: Theme) => ({
	importDialog: {
		'& .MuiDialogContent-root': {
			display: 'grid',
			gridTemplateColumns: '300px',
			gap: theme.spacing(2) + 'px',
			padding: 20,
		},
		'& .MuiPaper-root > div': {
			overflowY: 'hidden',
		},
	},
	hidden: {
		display: 'none',
	},
}));

interface IImportDialogProps {
	uploadPath: string
	afterImport(success: boolean) : void
}

enum EImportDialogState {NEW, WAITING, FEEDBACKS, SUCCESS, FAILURE}

export const ImportDialog = observer(function ImportDialog(props: IImportDialogProps) {
	const classes = useStyles();
	const omnistore = useOmnistore();
	const {uploadPath, afterImport} = props;
	const [file, setFile] = useState<File>();
	const cancelledAgg = useBooleanStateAgg();
	const requestedAgg = useBooleanStateAgg();
	const [response, setResponse] = useState<IOcResponse>();
	const ref = useRef<HTMLInputElement>(null);
	const feedbacks = response?.failure?.feedbacks;

	const state: EImportDialogState = (() => {
		if (cancelledAgg.value) return EImportDialogState.FAILURE;
		if (!response) return requestedAgg.value ? EImportDialogState.WAITING : EImportDialogState.NEW;
		if (feedbacks?.length) return EImportDialogState.FEEDBACKS;
		if (response.outputList) return EImportDialogState.SUCCESS;
		return EImportDialogState.FAILURE;
	})();
	console.log('STATE', EImportDialogState[state]);

	useEffect(() => {
		if (state === EImportDialogState.SUCCESS) {
			toasterSvc.success('importSuccess');
			afterImport(true);
		} else if (state === EImportDialogState.FAILURE) {
			if (response) toasterSvc.handleResponse('')(response);
			afterImport(false);
		}
	});

	function selectFile() {
		ref.current?.click();
	}
	function upload(confirmed: boolean) {
		setResponse(undefined);
		if (!file) return selectFile();
		requestedAgg.trueHandler();
		omnistore.communicator.doUpload(uploadPath, {confirmed}, file)
			.then(r => setResponse(r));
	}
	const onClick = () => upload(false);
	const execute = feedbacks?.map(f => f.level==='INFO' || f.level==='WARNING').reduce((a, b) => a && b) ? () => upload(true) : undefined;
	function cancel(e?: React.MouseEvent) {
		setResponse(undefined);
		toasterSvc.warning('cancelled');
		cancelledAgg.trueHandler(e);
	}

	return <>
		<Dialog open={true} onClose={cancelledAgg.trueHandler} className={classes.importDialog}>
			<DialogTitle>{transl(uploadPath + 'Dialog')}</DialogTitle>
			{
				state===EImportDialogState.WAITING ? <PleaseWait/>
					: <DialogContent>
						<input ref={ref}
							className={classes.hidden}
							type="file"
							onChange={e => setFile(e.currentTarget.files?.[0] || undefined)}
						/>
						<TextField
							variant="outlined"
							onClick={selectFile}
							value={file?.name || ''}
							label={transl('excelImportFile')}
							placeholder={transl('pleaseSelectFile')}
							onFocus={e => e.currentTarget.blur()}
							InputLabelProps={{shrink: true}}
						/>
						<Button
							variant={file ? 'contained' : 'outlined'}
							color="primary"
							onClick={onClick}
						>
							<ImportIcon/>
						</Button>
					</DialogContent>
			}
		</Dialog>
		{
			!feedbacks ? null :
				<ImportFeedbackDialog feedbacks={feedbacks} execute={execute} cancel={cancel}/>
		}
	</>;
});
